import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

const ClientCard = ({
  client,
  onViewProfile,
  onBookAppointment,
  onEditClient,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-success text-success-foreground";
      case "inactive":
        return "bg-muted text-muted-foreground";
      case "vip":
        return "bg-accent text-accent-foreground";
      default:
        return "bg-secondary text-secondary-foreground";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON",
    }).format(amount || 0);
  };

  return (
    <div className="bg-background border border-border rounded-lg p-6 hover-elevate transition-all duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Image
              src={
                client?.avatar ||
                `https://api.dicebear.com/7.x/initials/svg?seed=${client?.name}`
              }
              alt={client?.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            {client?.status === "vip" && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                <Icon name="Crown" size={12} color="white" />
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                className="text-left text-lg font-semibold text-card-foreground hover:underline"
                onClick={() => onViewProfile?.(client)}
              >
                {client?.name}
              </button>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  client?.status
                )}`}
              >
                {client?.status === "active"
                  ? t("clients.statusActive")
                  : client?.status === "inactive"
                  ? t("clients.statusInactive")
                  : client?.status === "vip"
                  ? t("clients.statusVip")
                  : client?.status}
              </span>
            </div>
            {client?.email && (
              <p className="text-sm text-muted-foreground">{client.email}</p>
            )}
            {client?.phone && (
              <p className="text-sm text-muted-foreground">{client.phone}</p>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowDetails((v) => !v)}
          className="text-muted-foreground hover:text-foreground"
        >
          <Icon
            name={showDetails ? "ChevronUp" : "ChevronDown"}
            size={20}
          />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">
            {client?.totalAppointments ?? client?.total_appointments ?? 0}

          </div>
          <div className="text-xs text-muted-foreground">{t("clients.appointmentsCount")}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-success">
            {formatCurrency(client?.totalSpent ?? client?.total_spent)}

          </div>
          <div className="text-xs text-muted-foreground">{t("clients.totalSpent")}</div>
        </div>
      </div>

      {/* Last visit */}
      <div className="flex items-center justify-between mb-4 p-3 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {t("clients.lastVisit")}
          </span>
        </div>
        <span className="text-sm font-medium text-foreground">
          {(client?.lastVisit || client?.last_visit)
            ? formatDate(client?.lastVisit || client?.last_visit)
            : t("clients.never")}
        </span>
      </div>

      {/* Expanded details */}
      {showDetails && (
        <div className="border-t border-border pt-4 space-y-4 animate-slide-up">
          {/* Preferred services */}
          {client?.preferredServices?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">
                {t("clients.preferredServices")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {client.preferredServices.map((service, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent appts */}
          {client?.recentAppointments?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">
                {t("clients.recentAppointments")}
              </h4>
              <div className="space-y-2">
                {client.recentAppointments.slice(0, 3).map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex items-center justify-between p-2 bg-muted/50 rounded"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon
                        name="Clock"
                        size={14}
                        className="text-muted-foreground"
                      />
                      <span className="text-sm text-foreground">
                        {appointment.service}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(appointment.date)}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          appointment.status === "completed"
                            ? "bg-success/10 text-success"
                            : appointment.status === "cancelled"
                            ? "bg-error/10 text-error"
                            : "bg-warning/10 text-warning"
                        }`}
                      >
                        {appointment.status === "completed"
                          ? t("calendar.statusCompleted")
                          : appointment.status === "cancelled"
                          ? t("calendar.statusCancelled")
                          : t("calendar.statusPending")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
        <Button
          variant="default"
          size="sm"
          onClick={() => onBookAppointment?.(client)}
          iconName="Plus"
          iconPosition="left"
          className="flex-1"
        >
          {t("clients.schedule")}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewProfile?.(client)}
          iconName="Eye"
          iconPosition="left"
          className="flex-1 hidden sm:inline-flex"
        >
          {t("clients.viewProfile")}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEditClient?.(client)}
        >
          <Icon name="Edit" size={16} />
        </Button>
      </div>
    </div>
  );
};

export default ClientCard;
